import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  UserMinus, Download, FileText, FileSpreadsheet, RefreshCw,
  AlertOctagon, TrendingDown, Calendar, Filter
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';

const API = '/api/hr/austritte';

// Farbpalette konform mit SKILL-design (Blau/Cyan/Indigo + gedämpfte Töne für Donut).
const DONUT_COLORS = [
  '#338EDC', '#22D3EE', '#6366F1',
  '#EAB308', '#F97316', '#DC2626',
  '#8B5CF6', '#14B8A6', '#EC4899',
  '#64748B', '#A3A3A3'
];

function fmtInt(n) {
  return Number.isFinite(n) ? n.toLocaleString('de-AT') : '—';
}
function fmtPct(n) {
  return Number.isFinite(n) ? `${n.toFixed(1)} %` : '—';
}
function fmtDauer(monateDezimal, t) {
  if (monateDezimal == null || !Number.isFinite(monateDezimal)) return '—';
  if (monateDezimal < 1) return t('austritt.dauer.unter_monat', 'Weniger als 1 Monat');
  const jahre = Math.floor(monateDezimal / 12);
  const monate = Math.floor(monateDezimal - jahre * 12);
  const jahrLabel = (n) => t(n === 1 ? 'austritt.dauer.jahr_one' : 'austritt.dauer.jahr_other', n === 1 ? 'Jahr' : 'Jahre');
  const monatLabel = (n) => t(n === 1 ? 'austritt.dauer.monat_one' : 'austritt.dauer.monat_other', n === 1 ? 'Monat' : 'Monate');
  if (jahre === 0) return `${monate} ${monatLabel(monate)}`;
  if (monate === 0) return `${jahre} ${jahrLabel(jahre)}`;
  return `${jahre} ${jahrLabel(jahre)} ${monate} ${monatLabel(monate)}`;
}
function maName(r) {
  return [r.nachname, r.vorname].filter(Boolean).join(', ') || '—';
}

function KpiCard({ label, value, sublabel, icon: Icon, accent = 'var(--text-primary)' }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{label}</div>
        {Icon && <Icon size={16} style={{ color: 'var(--text-tertiary)' }} />}
      </div>
      <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
      {sublabel && <div className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{sublabel}</div>}
    </div>
  );
}

export default function HrAustritte() {
  const { t } = useLanguage();
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [filterArt, setFilterArt] = useState('');
  const [exklAusschluss, setExklAusschluss] = useState(true); // Default-ON: Pensionierung/Tod/Zeitablauf raus
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState(null);
  const [verteilung, setVerteilung] = useState(null);
  const [liste, setListe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const jahre = useMemo(() => {
    const ny = new Date().getFullYear();
    return [ny, ny - 1, ny - 2, ny - 3, ny - 4];
  }, []);

  const AUFLOESUNGSARTEN = [
    'kuendigung_an', 'kuendigung_ag',
    'einvernehmlich_an', 'einvernehmlich_ag',
    'probezeit_an', 'probezeit_ag',
    'entlassung', 'vorzeitig', 'zeitablauf',
    'pensionierung', 'tod'
  ];

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `?jahr=${jahr}`;
      const qsListe = filterArt ? `${qs}&aufloesungsart=${encodeURIComponent(filterArt)}` : qs;
      const [rK, rT, rV, rL] = await Promise.all([
        fetch(`${API}/kpis${qs}`, { credentials: 'include' }),
        fetch(`${API}/trend?jahre=5`, { credentials: 'include' }),
        fetch(`${API}/verteilung${qs}`, { credentials: 'include' }),
        fetch(`${API}/liste${qsListe}`, { credentials: 'include' })
      ]);
      if (!rK.ok || !rT.ok || !rV.ok || !rL.ok) throw new Error(`HTTP ${rK.status}/${rT.status}/${rV.status}/${rL.status}`);
      setKpis(await rK.json());
      setTrend(await rT.json());
      setVerteilung(await rV.json());
      setListe(await rL.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jahr, filterArt]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const downloadExport = (typ) => {
    window.location.href = `${API}/export/${typ}?jahr=${jahr}`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <UserMinus size={24} />
            {t('austritte.title', 'Austritts-Report')}
          </h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-secondary)' }}>
            {t('austritte.subtitle', 'Fluktuations-Analyse, Auflösungsarten und Trend.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadAll()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[13px] hover:bg-[var(--bg-inset,#F8F9FC)]"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh', 'Aktualisieren')}
          </button>
          <button
            onClick={() => downloadExport('xlsx')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[13px] hover:bg-[var(--bg-inset,#F8F9FC)]"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
          >
            <FileSpreadsheet size={14} />
            Excel
          </button>
          <button
            onClick={() => downloadExport('pdf')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[13px] hover:bg-[var(--bg-inset,#F8F9FC)]"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
          >
            <FileText size={14} />
            PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* Filter-Bar */}
      <div className="bg-surface-card border border-line rounded-card p-3 mb-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Calendar size={14} style={{ color: 'var(--text-tertiary)' }} />
            <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{t('austritte.jahr', 'Jahr')}:</span>
            <select
              value={jahr}
              onChange={e => setJahr(parseInt(e.target.value))}
              className="bg-[var(--bg-input,#fff)] border rounded-lg px-2 py-1 text-[13px]"
              style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary)' }}
            >
              {jahre.map(j => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={14} style={{ color: 'var(--text-tertiary)' }} />
            <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{t('austritte.aufloesungsart', 'Auflösungsart')}:</span>
            <select
              value={filterArt}
              onChange={e => setFilterArt(e.target.value)}
              className="bg-[var(--bg-input,#fff)] border rounded-lg px-2 py-1 text-[13px]"
              style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary)' }}
            >
              <option value="">{t('austritte.filter.alle', 'Alle')}</option>
              {AUFLOESUNGSARTEN.map(a => (
                <option key={a} value={a}>{t(`austritt.aufloesungsart.${a}`, a)}</option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer ml-auto" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={exklAusschluss}
            onChange={e => setExklAusschluss(e.target.checked)}
          />
          {t('austritte.exkl_ausschluss', 'Pensionierung/Tod/Zeitablauf exkl.')}
          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
            ({t('austritte.fuer_fluktuation', 'für Fluktuations-KPI')})
          </span>
        </label>
      </div>

      {/* KPI-Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard
          label={t('austritte.kpi.austritte_ytd', 'Austritte')}
          value={fmtInt(kpis?.austritte_total)}
          sublabel={kpis ? `${t('austritte.kpi.exkl', 'exkl.')} ${fmtInt(kpis.austritte_exkl)}` : ''}
          icon={UserMinus}
        />
        <KpiCard
          label={t('austritte.kpi.fluktuation', 'Fluktuations-Quote')}
          value={kpis ? fmtPct(exklAusschluss ? kpis.fluktuation_exkl_pct : kpis.fluktuation_inkl_pct) : '—'}
          sublabel={kpis ? `${t('austritte.kpi.ma_durchschnitt', 'Ø MA-Stand')}: ${Math.round(kpis.ma_stand_durchschnitt)}` : ''}
          icon={TrendingDown}
        />
        <KpiCard
          label={t('austritte.kpi.unvollstaendig', 'Unvollständige Daten')}
          value={fmtInt(kpis?.unvollstaendig_anzahl)}
          sublabel={kpis?.unvollstaendig_anzahl > 0 ? (
            <Link to="/hr-audit" className="hover:underline" style={{ color: '#338EDC' }}>
              {t('austritte.kpi.zum_audit', 'zum HR-Audit →')}
            </Link>
          ) : t('austritte.kpi.alles_ok', 'alle vollständig')}
          icon={AlertOctagon}
          accent={kpis?.unvollstaendig_anzahl > 0 ? '#DC2626' : 'var(--text-primary)'}
        />
        <KpiCard
          label={t('austritte.kpi.probezeit', 'Probezeit-Anteil')}
          value={kpis ? fmtPct(kpis.probezeit_anteil_pct) : '—'}
          sublabel={kpis ? `${kpis.probezeit_anzahl} ${t('austritte.kpi.von', 'von')} ${kpis.austritte_total}` : ''}
        />
      </div>

      {/* Chart-Row: Trend (links) + Donut (rechts) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {/* Trend */}
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="mb-3">
            <h3 className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
              {t('austritte.trend.title', 'Fluktuations-Trend (5 Jahre)')}
            </h3>
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {exklAusschluss
                ? t('austritte.trend.exkl_hint', 'Exkl. Pensionierung/Tod/Zeitablauf.')
                : t('austritte.trend.inkl_hint', 'Inkl. aller Auflösungsarten.')}
            </p>
          </div>
          {trend?.daten?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend.daten} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="jahr" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} unit=" %" />
                <ReTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(v, name) => [`${v} %`, name === 'fluktuation_exkl_pct' ? t('austritte.trend.exkl', 'Exkl. Ausschluss') : t('austritte.trend.inkl', 'Inkl. alle')]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey={exklAusschluss ? 'fluktuation_exkl_pct' : 'fluktuation_inkl_pct'}
                  stroke="#338EDC"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#338EDC' }}
                  name={exklAusschluss ? t('austritte.trend.exkl', 'Exkl. Ausschluss') : t('austritte.trend.inkl', 'Inkl. alle')}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              {loading ? t('common.loading', 'Laden …') : t('austritte.noData', 'Keine Daten.')}
            </div>
          )}
        </div>

        {/* Donut */}
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="mb-3">
            <h3 className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
              {t('austritte.verteilung.title', 'Auflösungsarten')} — {jahr}
            </h3>
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {verteilung?.total != null
                ? `${verteilung.total} ${t('austritte.verteilung.austritte', 'Austritte')}`
                : t('common.loading', 'Laden …')}
            </p>
          </div>
          {verteilung?.daten?.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie
                    data={verteilung.daten}
                    dataKey="anzahl"
                    nameKey="label"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {verteilung.daten.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v, n, p) => [`${v} (${p?.payload?.anteil_pct?.toFixed(1)} %)`, n]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-[12px]">
                {verteilung.daten.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>{d.label}</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{d.anzahl}</span>
                    <span className="w-12 text-right" style={{ color: 'var(--text-tertiary)' }}>{d.anteil_pct?.toFixed(1)} %</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              {loading ? t('common.loading', 'Laden …') : t('austritte.noData', 'Keine Austritte in diesem Jahr.')}
            </div>
          )}
        </div>
      </div>

      {/* Tabelle */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
          <h3 className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
            {t('austritte.tabelle.title', 'Austritte')}{filterArt ? ` — ${t(`austritt.aufloesungsart.${filterArt}`, filterArt)}` : ''}
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {liste?.gesamt != null ? `${liste.gesamt} ${t('austritte.tabelle.zeilen', 'Zeilen')}` : t('common.loading', 'Laden …')}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.ma', 'Mitarbeiter')}</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.eintritt', 'Eintritt')}</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.austritt', 'Austritt')}</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.dauer', 'Dauer')}</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.aufloesung', 'Auflösungsart')}</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('austritte.col.begruendung', 'Begründung')}</th>
              </tr>
            </thead>
            <tbody>
              {liste?.daten?.length > 0 ? liste.daten.map((r, i) => (
                <tr key={i} className="border-b odd:bg-[var(--bg-inset,#F8F9FC)]/30 hover:bg-[var(--bg-inset,#F8F9FC)]" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
                  <td className="py-2 px-3">
                    <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: '#338EDC' }}>
                      {maName(r)}
                    </Link>
                    {r.ist_unvollstaendig && (
                      <span className="ml-2 text-[10px] font-semibold text-red-600">⚠</span>
                    )}
                  </td>
                  <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.eintritt_datum || '—'}</td>
                  <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.austritt_datum}</td>
                  <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{fmtDauer(r.dauer_monate, t)}</td>
                  <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>
                    {r.aufloesungsart_label || (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                        style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' }}>
                        {t('austritte.tabelle.fehlt', 'fehlt')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 max-w-xs truncate" title={r.austritt_begruendung || ''} style={{ color: 'var(--text-secondary)' }}>
                    {r.austritt_begruendung || (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                        style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' }}>
                        {t('austritte.tabelle.fehlt', 'fehlt')}
                      </span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    {loading ? t('common.loading', 'Laden …') : t('austritte.noData', 'Keine Austritte.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
